import {initializeApp} from 'firebase/app';
import {initializeAuth, browserLocalPersistence, browserSessionPersistence, inMemoryPersistence, connectAuthEmulator} from 'firebase/auth';
import {getStorage, connectStorageEmulator} from 'firebase/storage';
import {getFunctions, connectFunctionsEmulator} from 'firebase/functions';
import {initializeFirestore, connectFirestoreEmulator} from 'firebase/firestore';

import {getOptionalAnalytics} from '-/data/optionalAnalytics';

export const isDev = process.env.NODE_ENV === 'development';
const usingApiKey = process.env.REACT_APP_API_KEY;

const firebaseConfig = {...window.__FIREBASE_CONFIG__};

// The project ids that only ever exist in the local emulator suite. There is no
// real Firebase project behind either one — `embertracking-demo`'s was deleted
// with the demo site, and `embertracking-demo-2` never had one; they're just the
// namespaces the Firestore/Auth/Storage emulators key their data by.
//
// Two of them because the hub local stack (hub/bin/start-local.js) boots TWO
// department apps against the SAME emulator suite, so the hub dashboard has more
// than one member department to aggregate. An explicit list rather than a
// `startsWith('embertracking-demo')` prefix: this decides whether a browser
// talks to emulators instead of a real backend, so a future tenant id must not
// be able to fall into it by naming coincidence.
const DEMO_PROJECT_IDS = ['embertracking-demo', 'embertracking-demo-2'];

export const isDemoProject = pid => DEMO_PROJECT_IDS.includes(pid);

// E2E lane overrides. The parallel e2e runner (bin/e2e-local.js) serves ONE
// production build to N isolated emulator stacks and points each browser
// context at its own stack by injecting per-lane emulator ports + api URL via
// a Playwright init script (window.__E2E_OVERRIDES__ is set before any app
// code runs — see test/e2e/fixtures.js). Only honored when the served config
// is the demo project, so a production tenant can never be redirected. Pinned
// to `embertracking-demo` specifically rather than isDemoProject() above: the
// lane runner only ever serves that project, so there's no reason to widen the
// one gate that can repoint a production bundle's backend.
const e2eOverrides = (typeof window !== 'undefined'
    && window.__E2E_OVERRIDES__
    && firebaseConfig.projectId === 'embertracking-demo')
    ? window.__E2E_OVERRIDES__
    : null;

let apiKey = firebaseConfig.apiKey;
if (isDev && usingApiKey) {
    apiKey = process.env.REACT_APP_API_KEY;
    firebaseConfig.apiKey = apiKey;
}

export const projectId = firebaseConfig.projectId;
export const sentryDsn = firebaseConfig.sentryDsn;
// All production tenants share the same Cloud Run api at api.embertracking.com.
// firebase-configs/*.json may override this when a tenant needs a different
// endpoint.
export const apiUrl = (e2eOverrides && e2eOverrides.apiUrl)
    || firebaseConfig.apiUrl
    || 'https://api.embertracking.com';
export const app = initializeApp(firebaseConfig);

// When the dev server is reached via ngrok (or any non-loopback host) the
// browser can't talk to localhost emulator ports. vite.config.js proxies
// /__/auth, /__/api, /google.firestore.v1.Firestore, and /v0 to the matching
// emulators, so we route the SDKs through the same origin.
//
// The #734 dev container sets REACT_APP_FORCE_EMULATOR_PROXY=true to force the
// same routing even on localhost: the emulators run inside the container on
// ports that aren't published to the host, so the browser can only reach them
// through the one mapped Vite port (i.e. the same-origin proxy).
const forceProxy = process.env.REACT_APP_FORCE_EMULATOR_PROXY === 'true';
const isProxiedDev = isDev
    && typeof window !== 'undefined'
    && (forceProxy || !['localhost', '127.0.0.1', ''].includes(window.location.hostname));

const proxyHost = isProxiedDev ? window.location.host : null;
const proxySsl = isProxiedDev && window.location.protocol === 'https:';

// Firestore listen transport: long-polling, forced.
//
// Safari blank-page stalls of 20-45s (#698 follow-on). When the WebChannel back
// channel runs in STREAMING mode (CI=0 on the wire) it is a single long-lived
// response the client reads incrementally. On WebKit that incremental delivery
// does not happen — the body is simply not surfaced — so the client sits on an
// open, healthy request receiving nothing until `ChannelRequest`'s 45,000ms
// watchdog (`this.H = 45E3` in @firebase/webchannel-wrapper) fires, cancels and
// retries. Only then does the data land.
//
// Measured against a LOCAL EMULATOR over loopback, which excludes every network
// explanation:
//
//   Listen  RID=rpc  wait=2ms  recv=46,030ms   58 KB  cancelled
//   Write   RID=rpc  wait=2ms  recv=45,128ms    0 KB  cancelled
//
// The server answered in 2ms; the receive phase hung for 45s. The main thread
// was idle throughout (45-46 rendered frames per second, longest script record
// 188ms), so it is not blocked JS either. The observed gap of 45,131ms matches
// the watchdog constant exactly.
//
// `experimentalAutoDetectLongPolling` does not save us: it only samples at
// handshake time, and production captures show it choosing streaming (CI=0).
// Forcing long-polling removes the long-lived incremental read entirely — each
// batch is a complete HTTP response — and an A/B on the reproducing machine
// confirmed it: no stall with it on, immediate stall the moment it came off.
//
// The cost is extra round trips (a new request per batch rather than one held
// stream). That is the trade we want: Firestore treats long-polling as a
// first-class transport and falls back to it on many networks anyway, and a few
// extra requests beats a 45-second blank page.
//
// The two options are mutually exclusive — the SDK throws if both are set —
// hence a switch rather than a merge. Escape hatch, in case a tenant ever needs
// the old behaviour without a deploy (read once at init; needs a reload):
//
//   localStorage.setItem('fsTransport', 'autoDetect')   // pre-fix behaviour
//   localStorage.setItem('fsTransport', 'stream')       // streaming, no autodetect
//   localStorage.removeItem('fsTransport')              // back to forced long-polling
const readTransportOverride = () => {
    try {
        return typeof localStorage !== 'undefined' ? localStorage.getItem('fsTransport') : null;
    } catch {
        // Private mode / storage disabled — fall through to the default.
        return null;
    }
};

// The Chromium lane runner needs streaming against the local emulator. Its
// long-poll back channel can lose messages between responses: the failed Mac
// gear trace acknowledged sequence 70, then received 114, losing the category
// ADD acknowledgement and leaving onSnapshot pending forever. The other three
// loading failures had the same sequence gaps. Keep production's Safari fix;
// e2eOverrides is already restricted to the emulator-only demo project above.
const transportOverride = readTransportOverride() || (e2eOverrides ? 'stream' : null);
const transportSettings = transportOverride === 'autoDetect'
    ? {experimentalAutoDetectLongPolling: true}
    : transportOverride === 'stream'
        ? {experimentalAutoDetectLongPolling: false}
        : {experimentalForceLongPolling: true};

export const db = isProxiedDev
    ? initializeFirestore(app, {
        ignoreUndefinedProperties: true,
        host: proxyHost,
        ssl: proxySsl,
        ...transportSettings
    })
    : initializeFirestore(app, {
        ignoreUndefinedProperties: true,
        ...transportSettings
    });

// Firebase Analytics reports to a GA4 web data stream identified by
// measurementId. Tenants provisioned without Google Analytics linked have no
// GA4 property and no measurementId in their config (e.g. eaglevalleyrescue,
// #1133). Initializing analytics there is pointless (nothing receives the
// events) and forces a fetchDynamicConfig call to firebase.googleapis.com that
// only ever fails — surfacing as recurring "Failed to fetch" noise in Sentry.
// Also skip iOS embedded browsers with unreliable Installations storage.
// Call sites null-check, so analytics never gates authentication or rendering.
export const analytics = getOptionalAnalytics(app, firebaseConfig.measurementId);
// Auth persistence: localStorage first, NOT the default IndexedDB.
//
// `getAuth(app)` defaults its persistence list to
// `[indexedDBLocalPersistence, browserLocalPersistence, ...]`, and to pick the
// first available option the SDK runs an IndexedDB availability probe during
// init. On Safari/WebKit that probe can hang indefinitely (never resolves and
// never throws) when IndexedDB is opened during page load / under storage
// partitioning / in private browsing. When it hangs, auth init never
// completes, so `onAuthStateChanged` never fires and the app is stranded on the
// loading spinner until the bootstrap watchdog trips ("App stuck on loading
// screen", JRFD-LOG-V — all reports Safari, only initializingAuth stuck).
//
// We use email/password + custom-token sign-in only (no popup/redirect), so we
// can skip IndexedDB entirely and skip the popup/redirect resolver. localStorage
// persists auth tokens across reloads just as well and its availability check is
// a synchronous try/catch (throws in private mode, never hangs). inMemory is the
// final fallback so a browser that blocks all web storage still gets a working
// session-scoped auth instead of a hang.
export const auth = initializeAuth(app, {
    persistence: [browserLocalPersistence, browserSessionPersistence, inMemoryPersistence]
});
export const functions = getFunctions(app, 'northamerica-northeast1');
export const storage = getStorage(app);

// `|| e2eOverrides`: the lane runner tests a PRODUCTION build (no HMR, no
// dev-compile), where isDev is false — the injected overrides are what open
// the emulator-connect path there. Still demo-project-only via the inner gate.
if (isDev || e2eOverrides) {
    if (!usingApiKey && isDemoProject(projectId)) {
        if (isProxiedDev) {
            // The Auth SDK strips any path from this URL and uses host:port
            // only, so we pass the bare origin and rely on Vite proxying the
            // /identitytoolkit.googleapis.com / /securetoken.googleapis.com /
            // /www.googleapis.com / /emulator paths to the Auth emulator.
            connectAuthEmulator(auth, window.location.origin);
            // Firestore is already pointed at the proxy via initializeFirestore.
            // Storage SDK builds its base URL from host:port, so pass the page's
            // actual port (e.g. the container's mapped Vite port over http, or
            // the implicit 443 for ngrok over https) and patch _protocol/host so
            // the same origin is used — the proxy strips /v0 back to the emulator.
            const proxyPort = window.location.port
                ? parseInt(window.location.port, 10)
                : (proxySsl ? 443 : 80);
            connectStorageEmulator(storage, window.location.hostname, proxyPort);
            storage._protocol = proxySsl ? 'https' : 'http';
            storage.host = window.location.host;
            // Functions emulator is not proxied — the app routes all HTTP work
            // through the Cloud Run api (/__/api), so wiring it up isn't needed
            // for ngrok testing.
        } else {
            const localhost = '127.0.0.1';
            const lanePorts = (e2eOverrides && e2eOverrides.emulatorPorts) || {};

            connectAuthEmulator(auth, `http://${localhost}:${lanePorts.auth || parseInt(process.env.REACT_APP_EMULATOR_AUTH_PORT, 10) || 9099}`);
            connectFunctionsEmulator(functions, localhost, lanePorts.functions || parseInt(process.env.REACT_APP_EMULATOR_FUNCTIONS_PORT, 10) || 5001);
            connectFirestoreEmulator(db, localhost, lanePorts.firestore || parseInt(process.env.REACT_APP_EMULATOR_FIRESTORE_PORT, 10) || 8082);
            connectStorageEmulator(storage, localhost, lanePorts.storage || parseInt(process.env.REACT_APP_EMULATOR_STORAGE_PORT, 10) || 9199);
        }
    }
}

export default app;
